import { ICONS } from 'components/Icon';
import { Menu } from 'components/Menu';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { Platform, StyleProp, ViewStyle } from 'react-native';
import { useDispatch } from 'react-redux';
import { FileTypeGroupsEnum } from 'service/file/types';
import {
  doAseOpenAssetFiles,
  doAssetOpenFiles,
  doAssetFileGetPhotoFromPhoto,
  eventTypeFilesSelected,
  doAppFileGetFilesFromPicker,
  doAppFileGetImagesFromPicker,
  doLegacyFileUpload,
  eventLegacyFileUploaded,
} from 'store/actions';
import { FilesContext, FilesContextEnum } from 'types/types';
import { ButtonGroup } from 'components/ButtonGroup';
import { IconButton } from 'components/IconButton';

interface Props {
  context: FilesContext;
  promptMessage?: string;
  enableFileManager?: boolean;
  multiSelect?: boolean;
  onChange?: (fileIds: string[]) => void;
  style?: StyleProp<ViewStyle>;
  onRemove?: () => void;
  type?: 'images' | 'files';
  maximized?: boolean;
  removeText?: string;
  disabled?: boolean;
  glass?: boolean;
}

export const FileAddButtons: React.FC<Props> = ({
  context,
  enableFileManager = true,
  multiSelect = false,
  onChange,
  onRemove,
  promptMessage,
  style,
  type = 'files',
  maximized,
  removeText,
  disabled,
  glass = true,
}) => {
  const dispatch = useDispatch();
  const eventKey = React.useRef(randomKey());

  const handleAddFromFileManager = () =>
    context.context === FilesContextEnum.localAssessment ||
    context.context === FilesContextEnum.remoteAssessment
      ? dispatch(
          doAseOpenAssetFiles({
            limitSelectionToFileGroups:
              type === 'images' ? [FileTypeGroupsEnum.image] : undefined,
            multiSelect,
            promptMessage,
            eventKey: eventKey.current,
          }),
        )
      : dispatch(
          doAssetOpenFiles({
            limitSelectionToFileGroups:
              type === 'images' ? [FileTypeGroupsEnum.image] : undefined,
            assetId: context.assetId,
            multiSelect,
            promptMessage,
            eventKey: eventKey.current,
          }),
        );

  React.useEffect(() => {
    const events = [
      doAddEvent(eventTypeFilesSelected, (event) => {
        if (
          event.payload.eventKey === eventKey.current &&
          event.payload.fileIds.length
        ) {
          onChange?.(event.payload.fileIds);
        }
      }),
      doAddEvent(eventLegacyFileUploaded, (event) => {
        if (
          event.payload.eventKey === eventKey.current &&
          event.payload.filesSyncFormat
        ) {
          onChange?.(
            Object.values(event.payload.filesSyncFormat).map((f) => f.id),
          );
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [onChange]);

  const buttonChildren = [];
  const otherButtonsConfig: {
    key: string;
    text: string;
    icon: ICONS;
    onPress: () => void;
    disabled?: boolean;
    destructive?: boolean;
  }[] = [];

  buttonChildren.push(
    <IconButton
      key="camera"
      icon={ICONS.CameraLight}
      onPress={() =>
        context.context === FilesContextEnum.remoteAssessment
          ? dispatch(
              doLegacyFileUpload({
                type: 'camera',
                eventKey: eventKey.current,
                propertyId: context.assetId,
              }),
            )
          : dispatch(
              doAssetFileGetPhotoFromPhoto({
                assetId: context.assetId,
                eventKey: eventKey.current,
              }),
            )
      }
      glass={glass}
      disabled={disabled}
    />,
  );
  if (Platform.OS === 'web') {
    // Web
    if (type === 'images') {
      buttonChildren.push(
        <IconButton
          key="image"
          icon={ICONS.ImageLight}
          onPress={() =>
            context.context === FilesContextEnum.remoteAssessment
              ? dispatch(
                  doLegacyFileUpload({
                    type: 'image-picker',
                    eventKey: eventKey.current,
                    propertyId: context.assetId,
                    multiSelect,
                    allowVideo: false,
                  }),
                )
              : dispatch(
                  doAppFileGetImagesFromPicker({
                    assetId: context.assetId,
                    eventKey: eventKey.current,
                    multiSelect,
                    allowVideo: false,
                    returnType: 'saveToAsset',
                  }),
                )
          }
          glass={glass}
          disabled={disabled}
        />,
      );
    } else {
      buttonChildren.push(
        <IconButton
          key="file"
          icon={ICONS.FolderOpenLight}
          onPress={() =>
            context.context === FilesContextEnum.remoteAssessment
              ? dispatch(
                  doLegacyFileUpload({
                    type: 'file-picker',
                    eventKey: eventKey.current,
                    propertyId: context.assetId,
                    multiSelect,
                  }),
                )
              : dispatch(
                  doAppFileGetFilesFromPicker({
                    eventKey: eventKey.current,
                    returnType: 'saveToAsset',
                    assetId: context.assetId,
                    multiSelect,
                    allowedFileGroups: null,
                  }),
                )
          }
          glass={glass}
          disabled={disabled}
        />,
      );
    }
  } else {
    // Native
    buttonChildren.push(
      <IconButton
        key="image"
        icon={ICONS.ImageLight}
        onPress={() =>
          context.context === FilesContextEnum.remoteAssessment
            ? dispatch(
                doLegacyFileUpload({
                  type: 'image-picker',
                  eventKey: eventKey.current,
                  propertyId: context.assetId,
                  multiSelect,
                  allowVideo: type === 'files',
                }),
              )
            : dispatch(
                doAppFileGetImagesFromPicker({
                  assetId: context.assetId,
                  eventKey: eventKey.current,
                  multiSelect,
                  allowVideo: type === 'files',
                  returnType: 'saveToAsset',
                }),
              )
        }
        glass={glass}
        disabled={disabled}
      />,
    );
    if (type === 'files') {
      otherButtonsConfig.push({
        key: 'devicefiles',
        text: 'Choose from Device Files',
        icon: ICONS.FolderOpenLight,
        disabled,
        onPress: () =>
          context.context === FilesContextEnum.remoteAssessment
            ? dispatch(
                doLegacyFileUpload({
                  type: 'file-picker',
                  eventKey: eventKey.current,
                  propertyId: context.assetId,
                  multiSelect,
                }),
              )
            : dispatch(
                doAppFileGetFilesFromPicker({
                  eventKey: eventKey.current,
                  returnType: 'saveToAsset',
                  assetId: context.assetId,
                  multiSelect,
                  allowedFileGroups: null,
                }),
              ),
      });
    }
  }

  if (enableFileManager) {
    otherButtonsConfig.push({
      key: 'assetfiles',
      text: 'Choose from Asset',
      icon: ICONS.RBPlectrumLight,
      onPress: handleAddFromFileManager,
      disabled,
    });
  }

  if (onRemove) {
    otherButtonsConfig.push({
      key: 'remove',
      text: removeText || 'Remove',
      icon: ICONS.TrashCanLight,
      destructive: true,
      onPress: onRemove,
      disabled,
    });
  }

  if (otherButtonsConfig.length) {
    if (maximized) {
      buttonChildren.push(
        ...otherButtonsConfig.map((conf) => (
          <IconButton
            key={conf.key}
            onPress={conf.onPress}
            icon={conf.icon}
            disabled={conf.disabled}
            glass={glass}
          />
        )),
      );
    } else {
      buttonChildren.push(
        <Menu
          key="other"
          buttonConfig={{
            icon: ICONS.EllipsisLight,
          }}
          options={otherButtonsConfig}
          disabled={disabled}
        />,
      );
    }
  }

  if (!buttonChildren.length) {
    return null;
  }

  return (
    <ButtonGroup
      backgroundColor={theme.colors.white90}
      disabled={disabled}
      style={style}
      glass={glass}
    >
      {buttonChildren}
    </ButtonGroup>
  );
};
